import React, { useState } from 'react';
import { ScrollView, StyleSheet, View, Platform, SafeAreaView } from 'react-native';
import Toast from 'react-native-root-toast';
import { useDispatch } from 'react-redux';
import { openAlert } from '../../store/actions/alert';
import Icon from '../Icon';
import { changePassword } from '../../services/api/profile.service';
import InputForm from '../InputForm';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import BaseText from '../BaseText';
import BottomButton from '../BottomButton';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';

const CheckLabel = ({ check, label = '' }) => {
  return (
    <View style={styles.check}>
      <Icon
        name={check ? 'check' : 'minus'}
        solid
        color={check ? Color.primary : 'rgb(100,100,100)'}
        size={10}
      />
      <BaseText style={CommonStyles.ml3}>{label}</BaseText>
    </View>
  );
};

const validatePassword = (password) => {
  return {
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    character: /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password),
    length: password && 5 < password.length && 17 > password.length,
  };
};

const PASSWORD_MISMATCH_ERROR = 'current password does not match.';
const PASSWORD_CHANGE_SUCCESS = 'password successfully changed.';

const PasswordForm = ({ onSubmit }) => {
  const dispatch = useDispatch();
  const { tKey } = useComponentTKey('PasswordForm');
  const { t } = useTranslation(i18nNS.me);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [correctPassword, setCorrectPassword] = useState({
    lowercase: false,
    uppercase: false,
    number: false,
    character: false,
    length: false,
  });
  const [errorMessage, setErrorMessage] = useState({});

  const onChangeTextPassword = (text) => {
    if (errorMessage.password) {
      setErrorMessage({
        ...errorMessage,
        password: '',
      });
    }
    setPassword(text);
  };

  const onChangeTextNewPassword = (text) => {
    if (text) {
      setCorrectPassword(validatePassword(text));
    }
    if (errorMessage.newPassword) {
      setErrorMessage({
        ...errorMessage,
        newPassword: '',
      });
    }
    setNewPassword(text);
  };

  const onChangeTextRepeatPassword = (text) => {
    if (text.length >= 6) {
      setErrorMessage({
        ...errorMessage,
        repeatPassword: text === text ? '' : t('common:Error.samePassword'),
      });
    } else {
      if (errorMessage.repeatPassword) {
        setErrorMessage({
          ...errorMessage,
          repeatPassword: '',
        });
      }
    }
    setRepeatPassword(text);
  };

  const isValid = () => {
    const _errorMessage = {};

    if (!password) {
      _errorMessage.password = t('common:Error.requiredPassword');
    }
    if (!newPassword) {
      _errorMessage.newPassword = t('common:Error.requiredNewPassword');
    } else {
      const _validPassword = validatePassword(newPassword);
      if (!_validPassword.lowercase) {
        _errorMessage.newPassword = t('common:Error.lowercaseCharacter');
      }
      if (!_validPassword.uppercase) {
        _errorMessage.newPassword = _errorMessage.newPassword || '';
        _errorMessage.newPassword += t('common:Error.uppercaseCharacter');
      }
      if (!_validPassword.number) {
        _errorMessage.newPassword = _errorMessage.newPassword || '';
        _errorMessage.newPassword += t('common:Error.numberCharacter');
      }
      if (!_validPassword.character) {
        _errorMessage.newPassword = _errorMessage.newPassword || '';
        _errorMessage.newPassword += t('common:Error.specialCharacter');
      }
      if (!_validPassword.length) {
        _errorMessage.newPassword = _errorMessage.newPassword || '';
        _errorMessage.newPassword += t('common:Error.passwordLength');
      }
    }
    if (!repeatPassword) {
      _errorMessage.repeatPassword = t('common:Error.requiredRepeatPassword');
    }
    if (newPassword && repeatPassword && newPassword !== repeatPassword) {
      _errorMessage.repeatPassword = t('common:Error.samePassword');
    }

    setErrorMessage(_errorMessage);

    return Object.keys(_errorMessage).length === 0;
  };

  const onPressSubmit = async () => {
    if (!isValid()) {
      return;
    }

    try {
      const response = await changePassword({
        CurrentPassword: password,
        NewPassword: newPassword,
      });

      const normalizedResponse = response.trim().toLowerCase();

      if (normalizedResponse === PASSWORD_MISMATCH_ERROR) {
        dispatch(
          openAlert({
            title: t(tKey('CurrentPasswordDoesNotMatch')),
            confirmText: t(tKey('GotIt')),
            showProgress: false,
            onConfirmPressed: () => {
              return;
            },
          })
        );
      } else if (normalizedResponse === PASSWORD_CHANGE_SUCCESS) {
        dispatch(
          openAlert({
            title: t(tKey('PasswordUpdated')),
            message: t(tKey('PassworSuccessfullyChanged')),
            confirmText: t(tKey('GotIt')),
            showProgress: false,
            onConfirmPressed: () => {
              onSubmit();
            },
          })
        );
      }
    } catch (error) {
      console.error('Error changing password:', error);
    }
  };

  return (
    <SafeAreaView style={CommonStyles.flex1}>
      <ScrollView style={CommonStyles.py3}>
        <View style={styles.body}>
          <InputForm
            label={t(tKey('CurrentPassword'))}
            value={password}
            required={true}
            secure={true}
            errorMessage={errorMessage.password}
            onChangeText={onChangeTextPassword}
          />
          <InputForm
            label={t(tKey('NewPassword'))}
            value={newPassword}
            required={true}
            secure={true}
            maxLength={16}
            errorMessage={errorMessage.newPassword}
            onChangeText={onChangeTextNewPassword}
          />
          <InputForm
            label={t(tKey('ReEnterNewPassword'))}
            value={repeatPassword}
            required={true}
            maxLength={16}
            secure={true}
            errorMessage={errorMessage.repeatPassword}
            onChangeText={onChangeTextRepeatPassword}
          />
          <View style={styles.checkContainer}>
            <CheckLabel check={correctPassword.lowercase} label={t(tKey('OneLowercaseLetter'))} />
            <CheckLabel check={correctPassword.uppercase} label={t(tKey('OneUppercaseLetter'))} />
            <CheckLabel check={correctPassword.number} label={t(tKey('OneNumber'))} />
            <CheckLabel check={correctPassword.character} label={t(tKey('SpecialChar'))} />
            <CheckLabel check={correctPassword.length} label={t(tKey('MinChar'))} />
          </View>
        </View>
      </ScrollView>
      <BottomButton onPress={onPressSubmit} label={t(tKey('UpdatePassword'))} />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  body: {
    ...CommonProperties.px3,
  },
  checkContainer: {
    justifyContent: 'flex-start',
    ...CommonProperties.py3,
    ...CommonProperties.pl5,
  },
  check: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonProperties.py2,
  },
});

export default PasswordForm;
